import type {
  Batch,
  Category,
  CreateBatchRequest,
  CreateProductRequest,
  Product,
} from "@medicorepay/shared/types";
import apiClient from "../lib/api-client";

function mutationHeaders(changeReason?: string) {
  return {
    headers: {
      "Idempotency-Key": crypto.randomUUID(),
      ...(changeReason ? { "X-Change-Reason": changeReason } : {}),
    },
  };
}

function paise(value: number | undefined, field: string): string | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value) || !Number.isInteger(value * 100) || value < 0) {
    throw new Error(
      `${field} must be a non-negative amount with at most two decimals`,
    );
  }
  return String(Math.round(value * 100));
}

// Re-export types for backward compatibility
export type { Batch, Category, Product };
export type CreateProductDto = CreateProductRequest;
export type CreateBatchDto = CreateBatchRequest;

export interface CreateCategoryDto {
  name: string;
  description?: string;
  code?: string;
  isActive?: boolean;
}

export const productService = {
  // Products
  async getProducts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    categoryId?: string;
    scheduleType?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) {
    const response = await apiClient.get("/products", { params });
    return response.data;
  },

  async getProduct(id: string) {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
  },

  async getProductByBarcode(barcode: string) {
    const response = await apiClient.get(`/products/barcode/${barcode}`);
    return response.data;
  },

  async getProductStats() {
    const response = await apiClient.get("/products/stats");
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // CATEGORY SUGGESTIONS (Simple Text Field Approach)
  // ═══════════════════════════════════════════════════════════════════════════════

  async getCategorySuggestions(
    search?: string,
  ): Promise<{ categories: string[] }> {
    const response = await apiClient.get("/products/categories/suggestions", {
      params: { search },
    });
    return response.data;
  },

  async getSubcategorySuggestions(
    category?: string,
    search?: string,
  ): Promise<{ subcategories: string[] }> {
    const response = await apiClient.get(
      "/products/subcategories/suggestions",
      {
        params: { category, search },
      },
    );
    return response.data;
  },

  async getCategoryStats(): Promise<
    { category: string; count: number; percentage: string }[]
  > {
    const response = await apiClient.get("/products/categories/stats");
    return response.data;
  },

  async suggestHsnCode(
    category?: string,
  ): Promise<{ hsnCode: string; gstRate: number } | null> {
    const response = await apiClient.get("/products/hsn/suggest", {
      params: { category },
    });
    return response.data;
  },

  async createProduct(data: CreateProductDto): Promise<Product> {
    const response = await apiClient.post(
      "/products",
      data,
      mutationHeaders("Create product"),
    );
    return response.data;
  },

  // Alternate Medicine Search (Salt Composition)
  async findAlternates(productId: string) {
    const response = await apiClient.get(`/products/${productId}/alternates`);
    return response.data;
  },

  async searchBySaltComposition(salt: string) {
    const response = await apiClient.get("/products/salt-search", {
      params: { salt },
    });
    return response.data;
  },

  async updateProduct(
    id: string,
    data: Partial<CreateProductDto>,
  ): Promise<Product> {
    const response = await apiClient.patch(
      `/products/${id}`,
      data,
      mutationHeaders("Update product"),
    );
    return response.data;
  },

  async deleteProduct(id: string) {
    const response = await apiClient.delete(
      `/products/${id}`,
      mutationHeaders("Delete product"),
    );
    return response.data;
  },

  async bulkDeleteProducts(ids: string[]) {
    const response = await apiClient.post(
      "/products/bulk-delete",
      { ids },
      mutationHeaders("Bulk delete products"),
    );
    return response.data;
  },

  async exportProductsCSV(params?: {
    search?: string;
    category?: string;
    scheduleType?: string;
    status?: string;
  }) {
    const response = await apiClient.get("/products/export/csv", {
      params,
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Categories
  async getCategories(includeInactive = false) {
    const response = await apiClient.get("/categories", {
      params: { includeInactive },
    });
    return response.data;
  },

  async getCategory(id: string) {
    const response = await apiClient.get(`/categories/${id}`);
    return response.data;
  },

  async createCategory(data: CreateCategoryDto): Promise<Category> {
    const response = await apiClient.post(
      "/categories",
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async updateCategory(
    id: string,
    data: Partial<CreateCategoryDto>,
  ): Promise<Category> {
    const response = await apiClient.patch(
      `/categories/${id}`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async deleteCategory(id: string) {
    const response = await apiClient.delete(
      `/categories/${id}`,
      mutationHeaders(),
    );
    return response.data;
  },

  // Batches
  async getBatches(params?: {
    productId?: string;
    storeId?: string;
    inStock?: boolean;
    expiringSoon?: boolean;
  }) {
    const response = await apiClient.get("/batches", { params });
    return response.data;
  },

  async getBatch(id: string) {
    const response = await apiClient.get(`/batches/${id}`);
    return response.data;
  },

  async getExpiringBatches(days = 90) {
    const response = await apiClient.get("/batches/expiring", {
      params: { days },
    });
    return response.data;
  },

  async getExpiredBatches() {
    const response = await apiClient.get("/batches/expired");
    return response.data;
  },

  async updateBatch(id: string, data: Partial<CreateBatchDto>): Promise<Batch> {
    const correction: Record<string, unknown> = {
      effectiveAt: new Date().toISOString(),
      reason: "Batch metadata update",
    };
    if (data.batchNumber !== undefined)
      correction.batchNumber = data.batchNumber;
    if (data.expiryDate !== undefined) correction.expiryDate = data.expiryDate;
    if (data.mfgDate !== undefined) correction.manufacturedAt = data.mfgDate;
    const costPaise = paise(data.costPrice, "costPrice");
    const mrpPaise = paise(data.mrp, "mrp");
    if (costPaise !== undefined) correction.costPaise = costPaise;
    if (mrpPaise !== undefined) correction.mrpPaise = mrpPaise;
    const response = await apiClient.post(
      `/product-revival/inventory/lots/${id}/correct-metadata`,
      correction,
      mutationHeaders(),
    );
    return response.data as Batch;
  },

  async deleteBatch(id: string, reason = "Legacy batch deletion requested") {
    const response = await apiClient.post(
      `/product-revival/inventory/lots/${id}/retire`,
      { reason },
      mutationHeaders(),
    );
    return response.data;
  },

  // Product Packs
  async getProductPacks(productId: string): Promise<ProductPack[]> {
    const response = await apiClient.get(`/products/${productId}/packs`);
    return response.data;
  },

  async createProductPack(
    productId: string,
    data: CreateProductPackRequest,
  ): Promise<ProductPack> {
    const response = await apiClient.post(
      `/products/${productId}/packs`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },
};

export interface CreateProductPackRequest {
  packCode: string;
  packDescription: string;
  packLevel: "BASE" | "PRIMARY" | "SECONDARY" | "TERTIARY" | "QUATERNARY";
  packType?: string;
  conversionNumerator?: string;
  conversionDenominator?: string;
  unitsPerPack?: number;
  unitType: string;
  uqc?: string;
  purchasePrice: number;
  sellingPrice: number;
  mrp: number;
  ptr?: number;
  barcode?: string;
  parentPackId?: string;
  isDefault?: boolean;
  isPurchaseUnit?: boolean;
  isSaleUnit?: boolean;
  isDisplayUnit?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface ProductPack {
  id: string;
  productId: string;
  parentPackId?: string | null;
  packCode: string;
  packDescription: string;
  packLevel: string;
  packType: string;
  unitsPerPack: number;
  conversionNumerator: string;
  conversionDenominator: string;
  resolvedConversionNumerator: string;
  resolvedConversionDenominator: string;
  resolvedBaseUnitsPerPack: number;
  uqc: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  unitType: string;
  barcode?: string;
  purchasePrice: number;
  sellingPrice: number;
  mrp: number;
  isDefault: boolean;
  isActive: boolean;
  isPurchaseUnit: boolean;
  isSaleUnit: boolean;
  isDisplayUnit: boolean;
}
